import React from "react";
import { useLang } from "@/contexts/LanguageContext";
import { SEO } from "@/components/common/SEO";
import { COMPANY } from "@/i18n/translations";
import { LeadForm } from "@/components/common/LeadForm";
import { CheckCircle2, FileText, Clock, Headphones, BarChart3, Package, ShieldCheck } from "lucide-react";

const itemIcons = [Clock, Headphones, FileText, BarChart3, Package, ShieldCheck];

export default function AMC() {
  const { t } = useLang();
  const items = t("amc.items");

  return (
    <div data-testid="amc-page">
      <SEO
        title={`AMC & SLA Contracts — Predictable OPEX, Guaranteed Uptime | ${COMPANY.brand}`}
        description="Orion FM Annual Maintenance Contracts include preventive maintenance, 24/7 emergency response, dedicated account manager and monthly KPI reporting."
      />

      <section className="bg-white py-20 lg:py-28">
        <div className="container-orion grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="text-eyebrow">{t("amc.sectionLabel")}</div>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-7xl tracking-tighter font-medium leading-[1.02] text-slate-900">
              {t("amc.title")}
            </h1>
          </div>
          <div className="lg:col-span-4 lg:pb-3">
            <p className="text-slate-600 leading-relaxed">{t("amc.desc")}</p>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-slate-50">
        <div className="container-orion">
          <div className="text-eyebrow text-blue-700">{t("amc.includes")}</div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-2xl overflow-hidden">
            {items.map((label, i) => {
              const Icon = itemIcons[i] || CheckCircle2;
              return (
                <div
                  key={i}
                  data-testid={`amc-item-${i}`}
                  className="bg-white p-8 hover:bg-slate-50 transition-colors"
                >
                  <div className="w-11 h-11 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <p className="mt-5 text-base font-heading text-slate-900 leading-snug">{label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-28 bg-white">
        <div className="container-orion grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <div className="text-eyebrow text-blue-700">{t("amc.cta")}</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-slate-900 leading-tight">
              {t("ctaBanner.title")}
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">{t("ctaBanner.desc")}</p>
          </div>
          <div className="lg:col-span-6">
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-xl shadow-blue-900/5">
              <LeadForm pageSource="amc" defaultService="amc" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
